/**
 * Hata parmak izi — "aynı hata" sorusunun tek yanıtı.
 *
 * Seq olayları tek tek gösteriyor: aynı arıza bin kez tekrarladığında bin satır oluyor
 * ve hangi arızanın kaç kullanıcıyı vurduğu görünmüyor. Sentry'nin issue kavramının
 * karşılığı bu alan: aynı arızanın bütün tekrarları AYNI parmak izini taşır, Seq'te
 * `Fingerprint` alanına göre gruplamak yeterli olur.
 *
 * Kural sunucudaki `ErrorFingerprint` ile birebir aynı tutulur:
 *   tip | bize ait ilk yığın çerçevesi | sayılardan arındırılmış mesaj
 * Üçü birleştirilip kısa bir özete (FNV-1a, 8 karakter) indirilir — telefonda okunacak
 * kadar kısa, çakışmayacak kadar geniş.
 *
 * Mesajın normalleştirilmesi şart: "Kayıt 4213 bulunamadı" ile "Kayıt 9988 bulunamadı"
 * aynı arıza; ham metinle gruplansa her kayıt ayrı bir arıza gibi görünürdü.
 */

/** Yığın çerçevesinde bize ait OLMAYAN yollar — kütüphane içi kare hatayı ayırt etmez. */
const VENDOR = /node_modules|\/vendor|\/dx-(core|reporting|dashboard)|\/monaco|chunk-[A-Za-z0-9]{6,}/;

/** Sayı, GUID, tarih, tırnak içi metin ve uzun kimlikler → sabit yer tutucu. */
export function normalizeMessage(message: string): string {
  return (message || '')
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '{guid}')
    .replace(/\d{1,2}[./]\d{1,2}[./]\d{2,4}([ T]\d{2}:\d{2}(:\d{2})?)?/g, '{tarih}')
    .replace(/'[^']*'/g, "'{değer}'")
    .replace(/"[^"]*"/g, '"{değer}"')
    .replace(/\b\d+([.,]\d+)?\b/g, '{sayı}')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 200);
}

/**
 * Yığından bize ait ilk kareyi seçer: dosya adı + satır.
 *
 * Kütüphane içinden atılan hatada (DevExpress, axios) kütüphane karesi hep aynıdır ve
 * farklı arızaları birbirine karıştırır; bu yüzden önce bizim kodumuz aranır, hiç
 * bulunamazsa ilk kare kullanılır.
 */
export function topFrame(stack: string | null | undefined): string {
  const lines = (stack || '')
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.startsWith('at ') || l.includes('@'));
  if (lines.length === 0) return '';
  const own = lines.find((l) => !VENDOR.test(l));
  const line = own ?? lines[0];
  // "at fn (https://host/assets/App-a1b2.js:12:34)" → "App-a1b2.js:12"
  const m = line.match(/([^/\\() ]+\.(?:tsx?|jsx?|mjs)):(\d+)/i);
  if (m) return `${m[1]}:${m[2]}`;
  return line.replace(/^at\s+/, '').slice(0, 120);
}

/** FNV-1a — kısa, bağımlılıksız, dağılımı bu iş için yeterli. */
function hash(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).toUpperCase().padStart(8, '0');
}

export interface FingerprintInput {
  /** Hata tipi ya da olay kaynağı (Error.name, 'api', 'promise'…) */
  kind: string;
  message: string;
  stack?: string | null;
}

export function fingerprintOf({ kind, message, stack }: FingerprintInput): string {
  return hash([kind || 'Error', topFrame(stack), normalizeMessage(message)].join('|'));
}
